import { useCallback } from 'react';

import type { TrackEventSchema } from '@trello/analytics-event-schemas';
import type { SourceType } from '@trello/analytics-types';
import { Analytics } from '@trello/atlassian-analytics';

import type { EntrypointId } from './EntrypointId';

type FeedbackCollectorModalSubmittedTrackEventSchema = TrackEventSchema<{
  action: 'submitted';
  actionSubject: 'feedback';
  actionSubjectId: 'feedbackCollectorModal';
  source: SourceType;
  attributes: {
    entrypointId: {
      type: EntrypointId;
      description: 'Feedback collector entry point UUID (assigned by the Feedback Collector team)';
    };
  };
  _meta: {
    id: 48460;
    owner: 'trello-web-eng';
    tier: 1;
    description: 'User submitted feedback through the Atlassian feedback collector modal';
  };
}>;

type FeedbackCollectorHandlers = {
  onSubmitAnalyticsEvent: () => void;
  onCloseAnalyticsEvent: () => void;
};

type FeedbackCollectorAnalyticsProps = {
  source: SourceType;
  entrypointId: EntrypointId;
};

/**
 * React hook that provides analytics event handlers for the feedback collector modal.
 *
 * Returns memoized callbacks for tracking submission and close events
 * via {@link Analytics}.
 *
 * @param props - The analytics configuration for the feedback collector.
 * @param props.source - The analytics source screen or view that triggered the feedback collector.
 * @param props.entrypointId - The feedback collector entry point UUID assigned by the Feedback Collector team.
 * @returns An object containing `onSubmitAnalyticsEvent` and `onCloseAnalyticsEvent` callbacks.
 */
export const useFeedbackCollectorAnalytics = ({
  source,
  entrypointId,
}: FeedbackCollectorAnalyticsProps): FeedbackCollectorHandlers => {
  const onSubmitAnalyticsEvent = useCallback(() => {
    Analytics.sendTrackEvent<FeedbackCollectorModalSubmittedTrackEventSchema>({
      action: 'submitted',
      actionSubject: 'feedback',
      actionSubjectId: 'feedbackCollectorModal',
      source,
      attributes: {
        entrypointId,
      },
    });
  }, [source, entrypointId]);
  const onCloseAnalyticsEvent = useCallback(() => {
    Analytics.sendClosedComponentEvent({
      componentType: 'feedback',
      componentName: 'feedbackCollectorModal',
      source,
      attributes: {
        entrypointId,
      },
    });
  }, [source, entrypointId]);
  return {
    onSubmitAnalyticsEvent,
    onCloseAnalyticsEvent,
  };
};
